import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { ListenButton } from "./ListenButton";

const BEGINNING_IMAGES = [
  "/images/about/beginning-01.jpg",
  "/images/about/beginning-02.jpg",
  "/images/about/beginning-03.jpg",
  "/images/about/beginning-04.jpg",
  "/images/about/beginning-05.jpg"
];

export default function About() {
  const { t } = useTranslation();
  const whys = t("about.whys", { returnObjects: true }) || [];
  const reflections = t("about.beginningReflections", { returnObjects: true }) || [];
  const founderParas = t("about.founderParas", { returnObjects: true }) || [];
  const philosophy = t("about.philosophy", { returnObjects: true }) || [];
  const values = t("about.values", { returnObjects: true }) || [];

  return (
    <div className="py-12 space-y-24 md:space-y-32">

      <section className="max-w-4xl mx-auto px-6 text-center space-y-6">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("about.eyebrow")}</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-primary-custom tracking-tight leading-tight">{t("about.heading")}</h1>
        <p className="font-serif text-lg sm:text-xl italic text-secondary-custom max-w-2xl mx-auto leading-relaxed">&quot;{t("about.quote")}&quot;</p>
        <div className="flex justify-center pt-2">
          <ListenButton text={[t("about.heading"), t("about.quote"), t("about.intro"), ...whys.map((w) => `${w.q}. ${w.a}`), t("about.beginningIntro"), ...reflections, t("about.beginningClose"), t("about.founderHeading"), ...founderParas, t("about.philosophyIntro"), t("about.philosophyClose"), t("about.missionBody"), t("about.visionBody")].filter(Boolean).join(" ")} testId="about-page-listen" idleKey="listen.toPage" />
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 space-y-12">
        <p className="font-serif text-lg sm:text-xl text-secondary-custom leading-relaxed font-light text-center max-w-3xl mx-auto">{t("about.intro")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {whys.map((item, idx) => (
            <div key={idx} className="space-y-3 border-t border-subtle-custom pt-6">
              <h2 className="text-xl font-serif text-primary-custom">{item.q}</h2>
              <p className="font-sans text-xs sm:text-sm text-secondary-custom leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 space-y-16 md:space-y-24">
        <div className="max-w-2xl mx-auto text-center space-y-4">
          <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("about.chapterOne")}</span>
          <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-primary-custom">{t("about.beginningTitle")}</h2>
          <p className="font-serif text-base sm:text-lg italic text-secondary-custom leading-relaxed font-light">{t("about.beginningIntro")}</p>
        </div>
        <div className="space-y-16 md:space-y-28">
          {reflections.map((reflection, idx) => (
            <div key={idx} data-testid={`beginning-plate-${idx}`} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className={idx % 2 === 1 ? "md:order-2" : ""}>
                <div className="border border-subtle-custom bg-primary-custom p-3 sm:p-4 shadow-sm max-w-sm mx-auto">
                  <img src={BEGINNING_IMAGES[idx]} alt={t("about.beginningTitle")} loading="lazy" className="w-full h-auto" />
                </div>
              </div>
              <div className="space-y-4">
                <p className="font-serif text-lg sm:text-xl text-primary-custom leading-relaxed font-light">{reflection}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="max-w-2xl mx-auto text-center border-t border-subtle-custom pt-12">
          <p className="font-serif text-base sm:text-lg italic text-secondary-custom leading-relaxed font-light">{t("about.beginningClose")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="aspect-[4/5] overflow-hidden border border-subtle-custom relative">
          <img src="/images/about/founder-portrait.jpg" alt="Cristina Lima" className="w-full h-full object-cover" />
          <div className="absolute bottom-8 left-8 bg-primary-custom border border-subtle-custom p-6 max-w-xs">
            <span className="text-[9px] font-sans tracking-[0.25em] uppercase text-secondary-custom block mb-2">{t("about.founderLabel")}</span>
            <span className="font-serif text-sm font-semibold uppercase text-primary-custom">Cristina Lima</span>
          </div>
        </div>
        <div className="space-y-8">
          <div className="space-y-4">
            <span className="text-[10px] font-sans tracking-[0.2em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("about.founderEyebrow")}</span>
            <h2 className="text-3xl font-serif tracking-tight text-primary-custom">{t("about.founderHeading")}</h2>
          </div>
          <div className="space-y-6 font-serif text-sm sm:text-base text-secondary-custom leading-relaxed font-light">
            {founderParas.map((p, i) => (<p key={i}>{p}</p>))}
          </div>
        </div>
      </section>

      <section className="bg-surface-custom py-24 transition-colors duration-500 border-y border-subtle-custom">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-12">
          <div className="space-y-4">
            <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("about.philosophyEyebrow")}</span>
            <h2 className="text-3xl font-serif tracking-tight text-primary-custom">{t("about.philosophyHeading")}</h2>
            <p className="font-serif text-base sm:text-lg italic text-secondary-custom max-w-2xl mx-auto leading-relaxed font-light">{t("about.philosophyIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4 max-w-2xl mx-auto text-left pt-4">
            {philosophy.map((line, idx) => (
              <div key={idx} className="flex items-start space-x-3 border-b border-subtle-custom py-3">
                <span className="text-[var(--accent-bronze)] font-serif text-sm">✦</span>
                <span className="font-serif text-sm text-primary-custom">{line}</span>
              </div>
            ))}
          </div>
          <p className="font-serif text-base sm:text-lg text-primary-custom max-w-2xl mx-auto leading-relaxed pt-8 italic">&quot;{t("about.philosophyClose")}&quot;</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="border border-subtle-custom bg-surface-custom/30 p-10 space-y-4">
          <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("about.missionEyebrow")}</span>
          <p className="font-serif text-base sm:text-lg text-primary-custom leading-relaxed font-light">{t("about.missionBody")}</p>
        </div>
        <div className="border border-subtle-custom bg-surface-custom/30 p-10 space-y-4">
          <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("about.visionEyebrow")}</span>
          <p className="font-serif text-base sm:text-lg text-primary-custom leading-relaxed font-light">{t("about.visionBody")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 space-y-12">
        <div className="max-w-3xl space-y-4">
          <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("about.valuesEyebrow")}</span>
          <h2 className="text-3xl font-serif tracking-tight text-primary-custom">{t("about.valuesHeading")}</h2>
          <p className="font-serif text-sm sm:text-base text-secondary-custom leading-relaxed font-light">{t("about.valuesIntro")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {values.map((value, idx) => (
            <div key={idx} className="border border-subtle-custom bg-primary-custom p-6 flex items-start space-x-4 hover:border-[var(--accent-bronze)] transition-all duration-500">
              <span className="font-serif text-lg italic text-[var(--accent-bronze)]">{String(idx + 1).padStart(2, '0')}</span>
              <span className="font-serif text-sm text-primary-custom leading-snug">{value}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 text-center space-y-8">
        <h2 className="text-2xl sm:text-3xl font-serif tracking-tight text-primary-custom">{t("about.institutionHeading")}</h2>
        <p className="font-serif text-sm sm:text-base text-secondary-custom leading-relaxed font-light">{t("about.institutionBody")}</p>
        <div className="pt-4">
          <Link to="/start-here" data-testid="about-cta-link" className="inline-flex items-center space-x-2 border border-primary px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--accent-bronze)] hover:text-white hover:border-[var(--accent-bronze)] transition-all duration-300">
            <span>{t("about.startHere")}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

    </div>
  );
}
